import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  SvgIcon,
  styled,
  InputBase,
  CircularProgress
} from '@mui/material';
import ContentCard from './ContentCard';
import {
  colorLinkGrey,
  colorTextBlack,
  colorTextGrey
} from '../../config/theme';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  getFavouriteListAll,
  getHistoryListAll,
  revertResources
} from '../../redux/reducers/resourcesSlice';
import { useSelector } from 'react-redux';
import { ChevronRightIcon } from '../../utils/iconSvg';
import SnackBarBox from '../../components/SnackBarBox';
import { handleError } from '../../utils/utils';
import _ from 'lodash';
import { getGamificationPoints } from '../../redux/reducers/layoutSlice';

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,

  marginLeft: 0,
  // marginRight: 10,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(1),
    width: 'auto'
  }
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  // padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  right: 6,
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}));

let searchValue = '';
const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  width: '100%',
  height: '2rem',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 1.5),
    paddingRight: `calc(1em + ${theme.spacing(2)})`,
    transition: theme.transitions.create('width'),
    width: searchValue ? '28ch' : '0px',
    border: searchValue ? '1px solid #CBD2D9' : '',
    borderRadius: searchValue ? '8px' : '',
    [theme.breakpoints.up('sm')]: {
      '&:focus': {
        width: searchValue ? '28ch' : '28ch',
        border: '1px solid #CBD2D9',
        borderRadius: '8px'
      }
    }
  }
}));

const DateWiseViewAll = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [initialValues, setInitalValues] = useState({
    tabValue: 0,
    page: 0,
    size: 20,
    searchFilter: ''
  });

  const {
    historyListAll,
    favouriteListAll,
    isGetHistoryListAllSuccess,
    isGetFavouriteListAllSuccess,
    isUpdateLikeDislikeSuccess,
    updateLikeDislikeResponse,
    isUpdateLikeDislikeLoading,
    isGamificationLoading,
    isGamificationSuccess,
    isDeleteGamificationLoading,
    isDeleteGamificationSuccess
  } = useSelector(state => state.resource);

  const { authData } = useSelector(state => state.auth);
  const [content, setContent] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [changeKey, setChangeKey] = useState(false);

  useEffect(() => {
    dispatch(revertResources());
  }, []);

  useEffect(() => {
    if (!isGamificationLoading && isGamificationSuccess) {
      dispatch(getGamificationPoints(authData?.userId || 0));
    }
    if (!isDeleteGamificationLoading && isDeleteGamificationSuccess) {
      dispatch(getGamificationPoints(authData?.userId || 0));
    }
  }, [
    isGamificationLoading,
    isGamificationSuccess,
    isDeleteGamificationLoading,
    isDeleteGamificationSuccess
  ]);

  useEffect(() => {
    if (!hasMore || loading) return; // Prevent unnecessary API calls

    setLoading(true);

    const commonPayload = {
      userId: authData?.userId,
      contentType: 'All',
      startDate: location.state?.apiDate,
      endDate: location.state?.apiDate,
      dateType: 'selected_date',
      paginationRequest: {
        type: 'all',
        page,
        size: initialValues.size
      }
    };

    const action =
      location.pathname === '/content-space/history/all'
        ? getHistoryListAll
        : getFavouriteListAll;

    dispatch(
      action({ payload: commonPayload, search: initialValues.searchFilter })
    )
      .then(response => {
        if (response?.payload?.contentList?.length === 0) {
          setHasMore(false);
        }
        setLoading(false);
        setIsFirstLoad(false); // Set first load complete after API call
      })
      .catch(() => {
        setLoading(false);
        setIsFirstLoad(false);
      });
  }, [
    page,
    hasMore,
    loading,
    location.pathname,
    authData,
    initialValues.searchFilter
  ]);

  useEffect(() => {
    setHasMore(true);
    if (isGetHistoryListAllSuccess && historyListAll?.contentList) {
      // setContent(prev => [...prev, ...historyListAll.contentList]);
      if (!changeKey) {
        if (
          content.length + historyListAll.contentList.length >=
          historyListAll.totalCount
        ) {
          setHasMore(false);
        }
      }

      if (page === 0 || changeKey) {
        setChangeKey(false);
        setContent(historyListAll.contentList || []);
      } else {
        setContent(prev => [...prev, ...historyListAll.contentList]);
      }
    }

    if (isGetFavouriteListAllSuccess && favouriteListAll?.contentList) {
      // setContent(prev => [...prev, ...favouriteListAll.contentList]);
      if (!changeKey) {
        if (
          content.length + favouriteListAll.contentList.length >=
          favouriteListAll.totalCount
        ) {
          setHasMore(false);
        }
      }

      if (page === 0 || changeKey) {
        setChangeKey(false);
        setContent(favouriteListAll.contentList || []);
      } else {
        setContent(prev => [...prev, ...favouriteListAll.contentList]);
      }
    }

    setLoading(false);
  }, [
    historyListAll,
    favouriteListAll,
    isGetHistoryListAllSuccess,
    isGetFavouriteListAllSuccess
  ]);

  useEffect(() => {
    let timeoutId = null;

    const handleScroll = () => {
      if (!loading && hasMore) {
        const nearBottom =
          window.innerHeight + document.documentElement.scrollTop >=
          document.documentElement.scrollHeight - 100;

        if (nearBottom) {
          clearTimeout(timeoutId);
          timeoutId = setTimeout(() => {
            setPage(prevPage => {
              const maxPage =
                Math.ceil(historyListAll?.totalCount / initialValues.size) - 1;
              return prevPage < maxPage ? prevPage + 1 : prevPage;
            });
          }, 200);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timeoutId);
    };
  }, [loading, hasMore, historyListAll]);

  const debouncedSearch = _.debounce(term => {
    setInitalValues(prev => ({
      ...prev,
      searchFilter: term
    }));

    setPage(0);
    setContent([]);
    setHasMore(true);
  }, 300);

  const handleSearch = e => {
    const term = e.target.value;
    // setSearch(term);
    if (term.length >= 3 || term.length === 0) {
      debouncedSearch(term);
    }
  };

  // Update Like Code

  useEffect(() => {
    if (isUpdateLikeDislikeSuccess && updateLikeDislikeResponse) {
      dispatch(getGamificationPoints(authData?.userId || 0));
      setChangeKey(true);
      window.scrollTo({
        top: 0,
        behavior: 'smooth' // Optional: makes the scrolling smooth
      });
      setPage(0);

      const commonPayload = {
        userId: authData?.userId,
        contentType: 'All',
        startDate: location.state?.apiDate,
        endDate: location.state?.apiDate,
        dateType: 'selected_date',
        paginationRequest: {
          type: 'all',
          page: 0,
          size: initialValues.size
        }
      };

      const action =
        location.pathname === '/content-space/history/all'
          ? getHistoryListAll
          : getFavouriteListAll;

      dispatch(
        action({ payload: commonPayload, search: initialValues.searchFilter })
      )
        .then(response => {
          if (response?.payload?.contentList?.length === 0) {
            setHasMore(false);
          }
          setLoading(false);
          setIsFirstLoad(false); // Set first load complete after API call
        })
        .catch(() => {
          setLoading(false);
          setIsFirstLoad(false);
        });
    }
  }, [isUpdateLikeDislikeSuccess, isUpdateLikeDislikeLoading]);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: '12px',
          justifyContent: 'space-between'
        }}
      >
        <Box display={'flex'}>
          <Typography
            style={{
              fontSize: '24px',
              fontFamily: 'Lato',
              lineHeight: '32px',
              fontWeight: 700,
              color: colorLinkGrey,
              cursor: 'pointer'
            }}
            onClick={() => {
              location.pathname === '/content-space/history/all'
                ? navigate('/content-space/history')
                : location.pathname === '/content-space/favourites/all'
                  ? navigate('/content-space/favourites')
                  : '';
            }}
          >
            {location.pathname === '/content-space/history/all'
              ? 'History'
              : 'Favourites'}
          </Typography>
          <SvgIcon sx={{ height: 32, width: 32 }}>
            <ChevronRightIcon color={colorTextGrey} />
          </SvgIcon>
          <Typography
            style={{
              fontSize: '32px',
              fontFamily: 'Lato',
              lineHeight: '36px',
              fontWeight: 700,
              color: colorTextBlack
            }}
          >
            {location?.state?.displayDate || ''}
          </Typography>
        </Box>

        <Search>
          <SearchIconWrapper>
            <svg
              width="21"
              height="20"
              viewBox="0 0 21 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 17.5L14.375 13.875M16.3333 9.16667C16.3333 12.8486 13.3486 15.8333 9.66667 15.8333C5.98477 15.8333 3 12.8486 3 9.16667C3 5.48477 5.98477 2.5 9.66667 2.5C13.3486 2.5 16.3333 5.48477 16.3333 9.16667Z"
                stroke="#ABB4BD"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </SearchIconWrapper>
          <StyledInputBase
            onChange={e => {
              searchValue = e.target.value;
              handleSearch(e);
            }}
            placeholder="Search here…"
          />
        </Search>
      </Box>
      <Box
        sx={{
          marginBottom: 4,
          backgroundColor: '#FFFFFF',
          padding: '16px',
          borderRadius: '8px'
        }}
      >
        <Box display={'flex'} justifyContent={'space-between'}></Box>

        <Grid container spacing={2}>
          {loading && isFirstLoad ? ( // Show loader only for first load
            <Grid
              item
              lg={12}
              display={'flex'}
              justifyContent={'center'}
              marginY={2}
            >
              <CircularProgress />
            </Grid>
          ) : content.length > 0 ? (
            content?.map((content, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                <ContentCard video={content} sectionID={content?.section} />
              </Grid>
            ))
          ) : (
            !loading &&
            !isFirstLoad && ( // Prevent showing 'No content available' during first load
              <Grid
                item
                lg={12}
                display={'flex'}
                justifyContent={'center'}
                marginY={2}
              >
                <Typography fontWeight={600}>No content available</Typography>
              </Grid>
            )
          )}
        </Grid>
      </Box>

      {hasMore && content.length > 0 && (
        <Box display="flex" justifyContent="center" my={2}>
          <CircularProgress />
        </Box>
      )}

      {!hasMore && content.length > 0 && (
        <SnackBarBox
          type="success"
          message={handleError('All records have been loaded.')}
        />
      )}
    </>
  );
};

export default DateWiseViewAll;
